import { useCurrentVenueSlug, useGetVenue } from '@customer/features/venues';

import { useBasketStore } from '../stores';

/**
 * Exposes the current venue's order quantity limit in relation to the
 * basket contents.
 *
 * Preconditions: must be used under a /$venueSlug route (uses
 * useCurrentVenueSlug) and the user must be authenticated so the venue
 * query can resolve.
 *
 * Returns:
 * - maxOrderQuantity: the venue's max_order_quantity, or null when the
 *   venue has no limit (or is still loading).
 * - totalQuantity: summed quantity of all basket positions.
 * - isLimitReached: adding one more tin would exceed the limit.
 * - isOverLimit: the basket already exceeds the limit (e.g. persisted
 *   basket from before a limit was configured).
 *
 * @example
 * const { maxOrderQuantity, isLimitReached } = useBasketLimit();
 * const added = addPosition(artworkId, maxOrderQuantity);
 */
export const useBasketLimit = () => {
  const venueSlug = useCurrentVenueSlug();
  const { data: venue } = useGetVenue(venueSlug);
  const totalQuantity = useBasketStore((state) =>
    state.positions.reduce((sum, p) => sum + p.quantity, 0)
  );

  const maxOrderQuantity = venue?.max_order_quantity ?? null;
  const isLimitReached =
    maxOrderQuantity !== null && totalQuantity >= maxOrderQuantity;
  const isOverLimit =
    maxOrderQuantity !== null && totalQuantity > maxOrderQuantity;

  return { maxOrderQuantity, totalQuantity, isLimitReached, isOverLimit };
};
